import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Compass,
  Download,
  House,
  LogOut,
  Megaphone,
  Menu,
  MessagesSquare,
  Moon,
  PlayCircle,
  X,
} from "lucide-react";
import { useChat } from "../chat/ChatProvider";
import UserAvatar from "./UserAvatar";
import { MENU_BY_ROLE, findActiveHref } from "./sidebar";
import { useNotices } from "../notices/NoticeProvider";
import { useTours } from "../../tours/TourProvider";
import { goInstallChatApp } from "../../services/chatApp";
import { InstallBanner, canGetChatApp, useInstallState } from "./InstallGuide";
import { useLockBodyScroll } from "../../utils/viewport";
import { overlayIsOnTop, useBackToClose } from "../../utils/backToClose";

// Phones: the sidebar becomes a native-style app bar (logo, page title, profile) plus a bottom tab bar
// (Home, Chat, Notices, Menu). Menu opens a top-level menu that drops down under the app bar, holding the
// same entries as the sidebar plus the profile, theme, tours, install and sign-out controls that live in
// the desktop header.

const APP_BAR_HEIGHT = 56;
export const TAB_BAR_HEIGHT = 56; // also assumed by ChatDock (TAB_BAR_CLEARANCE) and the page padding in App.js
const BRAND_THEME_COLOR = "#002776"; // public/index.html; restored when leaving the phone layout
const BAR_THEME_COLOR = { light: "#ffffff", dark: "#111827" };

const press = "transition-colors active:bg-app-hover";
const barButton = `relative flex h-10 w-10 items-center justify-center rounded-full text-app-secondary ${press}`;

// Text fields bring up the keyboard; the tab bar steps aside while one is focused, like native apps.
const isTextField = (el) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !/^(checkbox|radio|button|submit|reset|file|range|color)$/i.test(el.type)));

function useTextFieldFocused() {
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    let timer = 0;
    const update = () => setFocused(isTextField(document.activeElement));
    // On focusout the next field (if any) isn't focused yet; check once focus has settled.
    const onFocusOut = () => {
      clearTimeout(timer);
      timer = setTimeout(update, 0);
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return focused;
}
const card = "overflow-hidden rounded-2xl border border-app-primary bg-app-card";
const row = `flex w-full items-center gap-3 px-4 min-h-[52px] text-left text-[15px] ${press}`;

function RowIcon({ icon: Icon, active, tone = "blue" }) {
  const tones = {
    blue: active ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
    violet: "bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
    slate: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300",
    red: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
  };
  return (
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}>
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
    </span>
  );
}

function SectionTitle({ children }) {
  return <h2 className="px-4 pb-1.5 pt-5 text-xs font-semibold uppercase tracking-wide text-app-muted">{children}</h2>;
}

function Switch({ on }) {
  return (
    <span
      aria-hidden="true"
      className={`relative ml-auto h-[30px] w-[50px] shrink-0 rounded-full transition-colors ${on ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
    >
      <span
        className={`absolute top-[3px] h-6 w-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[23px]" : "translate-x-[3px]"}`}
      />
    </span>
  );
}

function MobileMenu({ menu, activeHref, unreadNotices, profile, isDarkMode, onToggleTheme, onSignOut, onClose }) {
  const navigate = useNavigate();
  const tours = useTours();
  const [toursOpen, setToursOpen] = useState(false);
  const install = useInstallState();

  const links = menu.filter((e) => e.href);
  const sections = menu.filter((e) => e.items);
  const badgeFor = (entry) => (entry.badgeKey === "notices" ? unreadNotices || null : entry.badge ?? null);

  return (
    <motion.nav
      aria-label="Main navigation"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 z-[39] overflow-y-auto overscroll-contain bg-app-secondary px-3 pb-6"
      style={{
        top: `calc(${APP_BAR_HEIGHT}px + env(safe-area-inset-top))`,
        bottom: `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
      }}
    >
      {/* Profile */}
      <button
        type="button"
        onClick={() => {
          onClose();
          navigate("/profile", { replace: overlayIsOnTop() });
        }}
        className={`${card} mt-3 flex w-full items-center gap-3 p-3 text-left ${press}`}
      >
        <UserAvatar name={profile.name} src={profile.avatarSrc} seed={profile.id} size="lg" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-semibold text-app-heading">{profile.name}</span>
          <span className="block text-sm text-app-muted">{profile.role ? `${profile.role} · ` : ""}View profile</span>
        </span>
        <ChevronRight size={20} className="text-app-muted" aria-hidden="true" />
      </button>

      {/* Top-level pages as tiles */}
      {links.length > 0 && (
        <ul className="mt-3 grid grid-cols-2 gap-2">
          {links.map((entry) => {
            const active = entry.href === activeHref;
            const badge = badgeFor(entry);
            const Icon = entry.icon;
            return (
              <li key={entry.href}>
                <Link
                  to={entry.href}
                  replace={overlayIsOnTop()}
                  onClick={onClose}
                  aria-current={active ? "page" : undefined}
                  className={`relative flex h-full min-h-[84px] flex-col justify-between gap-2 rounded-2xl border p-3 transition-colors active:scale-[0.98] ${
                    active
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-app-primary bg-app-card text-app-primary active:bg-app-hover"
                  }`}
                >
                  <Icon size={22} strokeWidth={1.8} className={active ? "text-white" : "text-blue-600 dark:text-blue-400"} aria-hidden="true" />
                  <span className="text-sm font-medium leading-tight">{entry.label}</span>
                  {badge != null && (
                    <span
                      className={`absolute right-2.5 top-2.5 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                        active ? "bg-white text-blue-700" : "bg-blue-600 text-white"
                      }`}
                    >
                      {badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {/* Sections as grouped lists */}
      {sections.map((section) => (
        <section key={section.label}>
          <SectionTitle>{section.label}</SectionTitle>
          <ul className={`${card} divide-y divide-[color:var(--border-primary)]`}>
            {section.items.map((item) => {
              const active = item.href === activeHref;
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    replace={overlayIsOnTop()}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                    className={`${row} ${active ? "font-semibold text-blue-700 dark:text-blue-300" : "text-app-primary"}`}
                  >
                    <RowIcon icon={item.icon} active={active} />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    <ChevronRight size={18} className="text-app-muted" aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {/* Preferences */}
      <SectionTitle>Preferences</SectionTitle>
      <ul className={`${card} divide-y divide-[color:var(--border-primary)]`}>
        <li>
          <button type="button" role="switch" aria-checked={isDarkMode} onClick={onToggleTheme} className={`${row} text-app-primary`}>
            <RowIcon icon={Moon} tone="slate" />
            <span className="flex-1">Dark mode</span>
            <Switch on={isDarkMode} />
          </button>
        </li>
        {tours && tours.tours.length > 0 && (
          <li>
            <button type="button" onClick={() => setToursOpen((v) => !v)} aria-expanded={toursOpen} className={`${row} text-app-primary`}>
              <RowIcon icon={Compass} tone="violet" />
              <span className="flex-1">Feature tours</span>
              {tours.unseenCount > 0 && (
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                  {tours.unseenCount} new
                </span>
              )}
              <ChevronDown size={18} className={`text-app-muted transition-transform ${toursOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {toursOpen && (
              <ul className="border-t border-app-primary bg-app-secondary/60">
                {tours.tours.map((tour) => {
                  const done = tours.isDone(tour);
                  return (
                    <li key={tour.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          tours.startTour(tour.id);
                        }}
                        className={`flex w-full items-start gap-3 py-2.5 pl-[60px] pr-4 text-left ${press}`}
                      >
                        {done ? (
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-500" aria-label="Seen" />
                        ) : (
                          <PlayCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-500" aria-label="New" />
                        )}
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-app-primary">{tour.title}</span>
                          <span className="block text-xs text-app-muted">{tour.description}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        )}
        {!install.installed && canGetChatApp() && (
          <li>
            <button type="button" onClick={goInstallChatApp} className={`${row} text-app-primary`}>
              <RowIcon icon={Download} />
              <span className="min-w-0 flex-1">
                <span className="block">Get the IRG Chat app</span>
                <span className="block text-xs text-app-muted">Chat full screen, with notifications</span>
              </span>
            </button>
          </li>
        )}
      </ul>

      <div className={`${card} mt-5`}>
        <button type="button" onClick={onSignOut} className={`${row} font-medium text-red-600 dark:text-red-400`}>
          <RowIcon icon={LogOut} tone="red" />
          <span className="flex-1">Sign out</span>
        </button>
      </div>

      <p className="mt-6 text-center text-[11px] text-app-muted">Developed with 💖 by Proviloops International.</p>
    </motion.nav>
  );
}

function TabBar({ homeHref, pathname, menuOpen, onToggleMenu, unreadNotices }) {
  const totalUnread = useChat()?.totalUnread || 0;
  const hidden = useTextFieldFocused();
  const tabs = [
    { key: "home", label: "Home", icon: House, href: homeHref, active: pathname === homeHref },
    { key: "chat", label: "Chat", icon: MessagesSquare, href: "/admin/chat", badge: totalUnread, tour: "header-chat" },
    { key: "notices", label: "Notices", icon: Megaphone, href: "/notices", badge: unreadNotices },
  ];
  const tabClass = (active) =>
    `relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
      active ? "text-blue-600 dark:text-blue-400" : "text-app-muted active:text-app-secondary"
    }`;
  const badge = (count) =>
    count > 0 && (
      <span className="absolute -right-2.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-900">
        {count > 99 ? "99+" : count}
      </span>
    );

  if (hidden) return null;
  return (
    <nav
      aria-label="Tabs"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200/80 bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/90"
    >
      <div className="flex" style={{ height: TAB_BAR_HEIGHT }}>
        {tabs.map(({ key, label, icon: Icon, href, badge: count, tour, active }) => {
          const isActive = !menuOpen && (active ?? (pathname === href || pathname.startsWith(`${href}/`)));
          return (
            <Link
              key={key}
              to={href}
              replace={menuOpen && overlayIsOnTop()}
              onClick={() => menuOpen && onToggleMenu()}
              aria-current={isActive ? "page" : undefined}
              data-tour={tour}
              className={tabClass(isActive)}
            >
              <span className="relative">
                <Icon size={24} strokeWidth={isActive ? 2.1 : 1.8} aria-hidden="true" />
                {badge(count)}
              </span>
              {label}
            </Link>
          );
        })}
        <button type="button" onClick={onToggleMenu} aria-expanded={menuOpen} className={tabClass(menuOpen)}>
          <span className="relative">
            {menuOpen ? <X size={24} strokeWidth={2.1} aria-hidden="true" /> : <Menu size={24} strokeWidth={1.8} aria-hidden="true" />}
          </span>
          Menu
        </button>
      </div>
    </nav>
  );
}

function MobileAppBar({ title, profile, isDarkMode, onToggleTheme, onSignOut }) {
  const { pathname } = useLocation();
  const unreadNotices = useNotices()?.unreadCount || 0;
  const [open, setOpen] = useState(false);

  const menu = MENU_BY_ROLE[profile.rawRole] || [];
  const activeHref = findActiveHref(menu, pathname);
  const homeHref = menu.find((entry) => entry.href)?.href || "/";

  useLockBodyScroll(open);
  useBackToClose(open, () => setOpen(false));

  // Leaving the page (any link in the menu, or back/forward) closes the menu.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Browser chrome / status bar takes the app bar's colour, so the top of the screen reads as one surface.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return undefined;
    meta.setAttribute("content", isDarkMode ? BAR_THEME_COLOR.dark : BAR_THEME_COLOR.light);
    return () => meta.setAttribute("content", BRAND_THEME_COLOR);
  }, [isDarkMode]);

  return (
    <>
      <header
        className={`sticky top-0 z-40 border-b pt-[env(safe-area-inset-top)] backdrop-blur-md transition-colors ${
          open
            ? "border-app-primary bg-app-secondary"
            : "border-gray-200/80 bg-white/90 dark:border-gray-800 dark:bg-gray-900/90"
        }`}
      >
        <div className="flex items-center gap-1 px-2" style={{ height: APP_BAR_HEIGHT }}>
          <Link to={homeHref} aria-label="IRG home" className="block h-10 w-10 shrink-0 overflow-hidden rounded-md">
            {/* Same crop as the collapsed sidebar: the PNG has wide transparent margins. */}
            <img src="/assets/adminLogo.png" alt="IRG" className="h-10 max-w-none -ml-[22px] dark:brightness-0 dark:invert" />
          </Link>
          <h1 className="min-w-0 flex-1 truncate pl-1 text-[17px] font-semibold text-app-heading">{open ? "Menu" : title}</h1>
          <Link to="/profile" aria-label="My profile" data-tour="header-profile-menu" className={barButton}>
            <UserAvatar name={profile.name} src={profile.avatarSrc} seed={profile.id} size="sm" />
          </Link>
        </div>
      </header>

      <InstallBanner />

      <TabBar
        homeHref={homeHref}
        pathname={pathname}
        menuOpen={open}
        onToggleMenu={() => setOpen((v) => !v)}
        unreadNotices={unreadNotices}
      />

      <AnimatePresence>
        {open && (
          <MobileMenu
            menu={menu}
            activeHref={activeHref}
            unreadNotices={unreadNotices}
            profile={profile}
            isDarkMode={isDarkMode}
            onToggleTheme={onToggleTheme}
            onSignOut={onSignOut}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default MobileAppBar;
